/**
 * A picture the trainer chose, turned into the cover the backend accepts — decoded, scaled to at
 * most 1600 px on its longest edge, and re-encoded as JPEG at 0.8. The decisions are
 * `model/coverFile`'s; this is the canvas work they need.
 *
 * `createImageBitmap` decodes, and honours the picture's own orientation, so a phone photo lands the
 * right way up; re-encoding through a canvas writes only pixels, so the EXIF (location included)
 * does not travel. `OffscreenCanvas` where the browser has it, a detached `<canvas>` otherwise.
 */

import {
  COVER_OUTPUT_TYPE,
  COVER_QUALITY,
  coverFileName,
  coverTargetSize,
  coverTypeRefusal,
  encodedCoverRefusal,
  type CoverRefusal,
} from './coverFile';

export type PreparedCover =
  | { readonly ok: true; readonly file: File }
  | { readonly ok: false; readonly refusal: CoverRefusal };

async function encode(bitmap: ImageBitmap, width: number, height: number): Promise<Blob | null> {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height);
    const context = canvas.getContext('2d');
    if (context === null) return null;
    context.drawImage(bitmap, 0, 0, width, height);
    return canvas.convertToBlob({ type: COVER_OUTPUT_TYPE, quality: COVER_QUALITY });
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (context === null) return null;
  context.drawImage(bitmap, 0, 0, width, height);
  return new Promise((resolve) => {
    canvas.toBlob(resolve, COVER_OUTPUT_TYPE, COVER_QUALITY);
  });
}

/** The cover to send, or why this picture cannot be one. */
export async function prepareCover(file: File): Promise<PreparedCover> {
  const typeRefusal = coverTypeRefusal(file);
  if (typeRefusal !== null) return { ok: false, refusal: typeRefusal };

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return { ok: false, refusal: 'undecodable' };
  }

  try {
    const size = coverTargetSize(bitmap.width, bitmap.height);
    const blob = await encode(bitmap, size.width, size.height);
    if (blob === null) return { ok: false, refusal: 'undecodable' };
    const sizeRefusal = encodedCoverRefusal(blob);
    if (sizeRefusal !== null) return { ok: false, refusal: sizeRefusal };
    return {
      ok: true,
      file: new File([blob], coverFileName(file.name), { type: COVER_OUTPUT_TYPE }),
    };
  } finally {
    bitmap.close();
  }
}
